import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTheme } from '@/theme';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/common/DataTable';
import * as liteLogApi from '@/api/device/logs/liteLog';
import * as miniLogApi from '@/api/device/logs/miniLog';
import { formatDateTime } from '@/utils/date';

import {
  TabLayout,
  TabHeader,
  ErrorMessage,
  EmptyState,
  LoadingSkeleton,
  DownloadButtons,
} from '@/components/layout/TabLayout';
import { useLogTabState, useLogFetch } from './hooks/useLogTabState';

interface Device {
  serial_number: string;
  device_type?: string;
}

interface AlarmLogTabProps {
  device: Device;
  onExportData?: (data: {
    chartImageUrl?: string;
    tableData: any[];
    tableColumns: Array<{ header: string; accessorKey: string }>;
  }) => void;
  onDownloadPdf?: () => Promise<void>;
}

type GasAlarmLog = liteLogApi.LiteGasAlarmLog | miniLogApi.MiniGasAlarmLog;

export const AlarmLogTab: React.FC<AlarmLogTabProps> = ({
  device,
  onExportData,
  onDownloadPdf,
}) => {
  const { theme } = useTheme();

  const {
    loading, setLoading,
    error, setError, handleError,
    dateRange, setDateRange,
    isLite, isMini,
    shouldFetch, isMultiDayRange,
  } = useLogTabState({ device });

  const [logs, setLogs] = useState<GasAlarmLog[]>([]);
  const excelExportRef = React.useRef<(() => void) | null>(null);
  const chartRef = useRef<any>(null);

  const fetchGasAlarmLogs = async () => {
    if (!device.serial_number || !dateRange.from || !dateRange.to) return;

    setLoading(true);
    setError(null);

    try {
      let data: GasAlarmLog[] = [];
      if (isLite) {
        data = await liteLogApi.getGasAlarmLogsByDevice(
          device.serial_number,
          dateRange.from,
          dateRange.to,
          10000
        );
      } else if (isMini) {
        data = await miniLogApi.getGasAlarmLogsByDevice(
          device.serial_number,
          dateRange.from,
          dateRange.to,
          10000
        );
      }

      setLogs(data);
    } catch (err: unknown) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  useLogFetch({
    shouldFetch,
    deps: [device.serial_number, device.device_type, dateRange.from, dateRange.to],
    fetchFn: fetchGasAlarmLogs,
  });

  const getAlarmTypeColor = (log: GasAlarmLog): string => {
    if ('alarm_type' in log) {
      // Lite
      switch (log.alarm_type) {
        case 1:
          return theme.colors.status.caution.fill.surface; // Low
        case 2:
          return theme.colors.status.danger.fill.surface; // High
        case 3:
          return theme.colors.status.caution.fill.surface; // STEL
        case 4:
          return theme.colors.status.caution.fill.surface; // TWA
        case 5:
          return theme.colors.status.danger.fill.surface; // Overrange
        default:
          return theme.colors.text.secondary;
      }
    } else {
      // Mini
      switch (log.event_type) {
        case 1:
          return theme.colors.status.caution.fill.text; // Low alarm
        case 2:
          return theme.colors.status.danger.fill.text; // High alarm
        case 3:
          return theme.colors.status.caution.fill.text; // STEL alarm
        case 4:
          return theme.colors.status.caution.fill.text; // TWA alarm
        case 5:
          return theme.colors.status.danger.fill.text; // Overrange alarm
        default:
          return theme.colors.text.secondary;
      }
    }
  };

  const getAlarmTypeLabel = (log: GasAlarmLog): string => {
    if ('alarm_type_label' in log) {
      return log.alarm_type_label;
    } else {
      return log.event_type_text;
    }
  };

  const getPeakConcentration = (log: GasAlarmLog): number => {
    if ('peak_concentration' in log) {
      return log.peak_concentration ?? 0;
    } else {
      return log.peak_conc;
    }
  };

  const getConcentrationUnit = (log: GasAlarmLog): string => {
    if ('concentration_unit_label' in log) {
      return log.concentration_unit_label;
    } else {
      return 'ppm'; // Mini는 기본 ppm
    }
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
  };

  const formatDateTimeStr = (dateString: string): string => {
    return formatDateTime(dateString);
  };

  // 엑셀 다운로드를 위한 플랫 데이터 구조
  interface FlatGasAlarmLog {
    start_time: string;
    start_time_formatted: string;
    alarm_type_label: string;
    sensor_name: string;
    peak_concentration: number;
    concentration_unit: string;
    peak_display: string;
    duration: number;
    duration_formatted: string;
    temperature: number | null;
    temperature_display: string;
    battery_voltage: number | null;
    battery_display: string;
  }

  const flattenedLogs = useMemo<FlatGasAlarmLog[]>(() => {
    return logs.map(log => {
      const peak = getPeakConcentration(log);
      const unit = getConcentrationUnit(log);
      const voltage = log.battery_voltage;
      const batteryDisplay =
        voltage === null
          ? '-'
          : typeof voltage === 'number'
            ? voltage > 100
              ? `${voltage}mV`
              : `${voltage.toFixed(1)}V`
            : '-';

      return {
        start_time: log.start_time,
        start_time_formatted: formatDateTimeStr(log.start_time),
        alarm_type_label: getAlarmTypeLabel(log),
        sensor_name: log.sensor_name,
        peak_concentration: peak,
        concentration_unit: unit,
        peak_display: `${peak.toFixed(1)} ${unit}`,
        duration: log.duration,
        duration_formatted: formatDuration(log.duration),
        temperature: log.temperature,
        temperature_display: log.temperature !== null ? `${log.temperature.toFixed(1)}°C` : '-',
        battery_voltage: voltage,
        battery_display: batteryDisplay,
      };
    });
  }, [logs]);

  // 테이블 컬럼 정의
  const columns = useMemo<ColumnDef<FlatGasAlarmLog>[]>(
    () => [
      {
        accessorKey: 'start_time_formatted',
        header: () => 'Time',
        size: 180,
      },
      {
        accessorKey: 'alarm_type_label',
        header: () => 'Alarm Type',
        cell: ({ row }) => {
          const log = logs.find(
            l => formatDateTimeStr(l.start_time) === row.original.start_time_formatted
          );
          if (!log) return row.original.alarm_type_label;

          const color = getAlarmTypeColor(log);
          const label = row.original.alarm_type_label;
          return (
            <span
              style={{
                padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
                borderRadius: theme.borderRadius.sm,
                backgroundColor: color + '20',
                color: color,
                fontSize: theme.fontSize.xs,
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </span>
          );
        },
        size: 120,
      },
      {
        accessorKey: 'sensor_name',
        header: () => 'Sensor',
        size: 120,
      },
      {
        accessorKey: 'peak_display',
        header: () => 'Peak',
        cell: ({ row }) => {
          const peak = row.original.peak_concentration;
          const unit = row.original.concentration_unit;
          return (
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontWeight: 600 }}>{peak.toFixed(1)}</span>{' '}
              <span
                style={{
                  color: theme.colors.text.secondary,
                  fontSize: theme.fontSize.xs,
                }}
              >
                {unit}
              </span>
            </div>
          );
        },
        size: 120,
      },
      {
        accessorKey: 'duration_formatted',
        header: () => 'Duration',
        cell: ({ getValue }) => <div style={{ textAlign: 'right' }}>{getValue() as string}</div>,
        size: 120,
      },
      {
        accessorKey: 'temperature_display',
        header: () => 'Temp (°C)',
        cell: ({ getValue }) => <div style={{ textAlign: 'right' }}>{getValue() as string}</div>,
        size: 100,
      },
      {
        accessorKey: 'battery_display',
        header: () => 'Battery',
        cell: ({ getValue }) => <div style={{ textAlign: 'right' }}>{getValue() as string}</div>,
        size: 100,
      },
    ],
    [logs, theme]
  );

  // PDF 생성을 위한 데이터 export
  useEffect(() => {
    if (!onExportData) return;
    if (loading) return;
    if (logs.length === 0) {
      onExportData({
        tableData: [],
        tableColumns: [],
      });
      return;
    }

    const prepareExportData = async () => {
      let chartImageUrl: string | undefined;

      // 차트 이미지 추출
      if (chartRef.current) {
        try {
          await new Promise(resolve => setTimeout(resolve, 1000));

          const echartsInstance = chartRef.current.getEchartsInstance();
          if (echartsInstance) {
            const isReady = echartsInstance.getWidth() > 0 && echartsInstance.getHeight() > 0;
            if (isReady) {
              chartImageUrl = echartsInstance.getDataURL({
                type: 'png',
                pixelRatio: 2,
              });
            }
          }
        } catch (error) {
          console.error('[AlarmLogTab] Failed to get chart image:', error);
        }
      }

      // 테이블 데이터 준비
      const tableData = flattenedLogs.map(row => ({
        Time: row.start_time_formatted,
        'Alarm Type': row.alarm_type_label,
        Sensor: row.sensor_name,
        Peak: row.peak_display,
        Duration: row.duration_formatted,
        'Temp (°C)': row.temperature_display,
        Battery: row.battery_display,
      }));

      const tableColumns = [
        { header: 'Time', accessorKey: 'Time' },
        { header: 'Alarm Type', accessorKey: 'Alarm Type' },
        { header: 'Sensor', accessorKey: 'Sensor' },
        { header: 'Peak', accessorKey: 'Peak' },
        { header: 'Duration', accessorKey: 'Duration' },
        { header: 'Temp (°C)', accessorKey: 'Temp (°C)' },
        { header: 'Battery', accessorKey: 'Battery' },
      ];

      onExportData({
        chartImageUrl,
        tableData,
        tableColumns,
      });
    };

    const timer = setTimeout(prepareExportData, 1000);
    return () => clearTimeout(timer);
  }, [onExportData, logs, flattenedLogs, loading]);

  // 이벤트 타임라인 차트 옵션 생성
  const eventTimelineChartOption = useMemo((): EChartsOption => {
    if (logs.length === 0) {
      return {};
    }

    // 1. 센서별로 그룹화 (동적으로 센서 추출)
    const sensorMap = new Map<string, GasAlarmLog[]>();
    logs.forEach(log => {
      const sensorName = log.sensor_name;
      if (!sensorMap.has(sensorName)) {
        sensorMap.set(sensorName, []);
      }
      sensorMap.get(sensorName)!.push(log);
    });

    // 센서 이름 목록 (Y축 카테고리)
    const sensorNames = Array.from(sensorMap.keys()).sort();

    // 2. 알람 타입별 색상 매핑
    const getAlarmColor = (log: GasAlarmLog): string => {
      if ('alarm_type' in log) {
        // Lite
        switch (log.alarm_type) {
          case 1:
            return theme.colors.status.caution.fill.surface; // Low - 주황
          case 2:
            return theme.colors.status.danger.fill.surface; // High - 빨강
          case 3:
            return theme.colors.status.caution.fill.surface; // STEL
          case 4:
            return theme.colors.status.caution.fill.surface; // TWA
          case 5:
            return theme.colors.status.danger.fill.surface; // Overrange
          default:
            return theme.colors.text.secondary;
        }
      } else {
        // Mini
        switch (log.event_type) {
          case 1:
            return theme.colors.status.caution.fill.surface; // Low alarm - 주황
          case 2:
            return theme.colors.status.danger.fill.surface; // High alarm - 빨강
          case 3:
            return theme.colors.status.caution.fill.surface; // STEL alarm
          case 4:
            return theme.colors.status.caution.fill.surface; // TWA alarm
          case 5:
            return theme.colors.status.danger.fill.surface; // Overrange alarm
          default:
            return theme.colors.text.secondary;
        }
      }
    };

    // 3. 알람 타입별로 시리즈 분리 (범례에 표시하기 위해)
    const alarmTypeMap = new Map<string, any[]>();

    logs.forEach(log => {
      const alarmType = getAlarmTypeLabel(log);
      const sensorName = log.sensor_name;

      if (!alarmTypeMap.has(alarmType)) {
        alarmTypeMap.set(alarmType, []);
      }

      const startTime = new Date(log.start_time).getTime();
      const color = getAlarmColor(log);

      // symbolSize는 duration 값에 비례 (최소 10, 최대 50)
      // duration이 0~300초(5분) 범위를 10~50 크기로 매핑
      const symbolSize = Math.max(10, Math.min(50, (log.duration / 300) * 40 + 10));

      // ECharts scatter with category yAxis: use [x, y] where y is the category name or index
      const dataPoint = {
        value: [startTime, sensorName], // Y축에 센서 이름 직접 사용
        symbolSize: symbolSize,
        itemStyle: { color },
        log, // 원본 로그 데이터 저장 (툴팁용)
      };

      alarmTypeMap.get(alarmType)!.push(dataPoint);
    });

    // 4. 알람 타입별 시리즈 생성
    const series: any[] = [];
    alarmTypeMap.forEach((data, alarmType) => {
      // 해당 알람 타입의 첫 번째 데이터에서 색상 추출
      const color = data[0]?.itemStyle?.color || theme.colors.text.secondary;

      series.push({
        name: alarmType,
        type: 'scatter',
        data: data,
        symbolSize: (dataItem: any) => {
          return dataItem.symbolSize || 20;
        },
        itemStyle: {
          color: color,
          opacity: 0.85,
        },
        label: {
          show: false,
        },
        emphasis: {
          focus: 'series',
          scale: 1.3,
          itemStyle: {
            opacity: 1,
            borderWidth: 2,
            shadowBlur: 10,
            shadowColor: color,
          },
        },
      });
    });

    // 5. 차트 옵션 구성
    const chartOption = {
      backgroundColor: 'transparent',
      title: {
        text: 'Event Timeline',
        subtext: `Total: ${logs.length} events`,
        left: '2%',
        top: '2%',
        textStyle: {
          fontSize: 20,
          fontWeight: 'bold',
          color: theme.colors.text.primary,
        },
        subtextStyle: {
          fontSize: 13,
          color: theme.colors.text.secondary,
        },
      },
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const data = params.data;
          if (!data || !data.log) return '';

          const log = data.log as GasAlarmLog;
          const alarmType = getAlarmTypeLabel(log);
          const peak = getPeakConcentration(log);
          const unit = getConcentrationUnit(log);
          const duration = formatDuration(log.duration);
          const startTime = formatDateTime(log.start_time);

          return `
            <div style="padding: 8px;">
              <div style="font-weight: bold; margin-bottom: 4px;">${log.sensor_name}</div>
              <div style="color: ${data.itemStyle.color}; font-weight: 600; margin-bottom: 4px;">${alarmType}</div>
              <div>Time: ${startTime}</div>
              <div>Peak: ${peak.toFixed(1)} ${unit}</div>
              <div>Duration: ${duration}</div>
              ${log.temperature !== null ? `<div>Temp: ${log.temperature.toFixed(1)}°C</div>` : ''}
            </div>
          `;
        },
      },
      legend: {
        show: true,
        top: '2%',
        right: '2%',
        orient: 'vertical',
        data: Array.from(alarmTypeMap.keys()),
        itemWidth: 25,
        itemHeight: 18,
        textStyle: {
          fontSize: 15,
          fontWeight: 600,
          color: theme.colors.text.primary,
        },
        itemGap: 15,
        icon: 'circle',
        padding: [5, 10],
        borderRadius: 4,
      },
      grid: {
        left: '8%',
        right: '20%',
        top: '15%',
        bottom: '18%',
        containLabel: true,
      },
      xAxis: {
        type: 'time',
        name: 'Time',
        nameLocation: 'middle',
        nameGap: 35,
        nameTextStyle: {
          fontWeight: 'bold',
          fontSize: 15,
          color: theme.colors.text.primary,
        },
        axisLabel: {
          fontSize: 14,
          margin: 12,
          rotate: 45,
          color: theme.colors.text.primary,
          formatter: (value: number) => {
            const date = new Date(value);
            if (isMultiDayRange) {
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              const hours = String(date.getHours()).padStart(2, '0');
              const minutes = String(date.getMinutes()).padStart(2, '0');
              return `${month}-${day} ${hours}:${minutes}`;
            } else {
              const hours = String(date.getHours()).padStart(2, '0');
              const minutes = String(date.getMinutes()).padStart(2, '0');
              return `${hours}:${minutes}`;
            }
          },
        },
        axisLine: { lineStyle: { color: theme.colors.border.container } },
        axisTick: { lineStyle: { color: theme.colors.border.container } },
        splitLine: {
          show: true,
          lineStyle: {
            color: theme.colors.border.divider,
            type: 'dashed',
          },
        },
      },
      yAxis: {
        type: 'category',
        name: 'Sensor',
        data: sensorNames,
        nameTextStyle: {
          fontWeight: 'bold',
          fontSize: 15,
          color: theme.colors.text.primary,
        },
        axisLabel: {
          interval: 0,
          fontSize: 16,
          fontWeight: 600,
          margin: 15,
          color: theme.colors.text.primary,
        },
        axisLine: {
          lineStyle: {
            width: 2,
            color: theme.colors.border.container,
          },
        },
        axisTick: {
          length: 8,
          lineStyle: {
            width: 2,
            color: theme.colors.border.container,
          },
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: theme.colors.border.divider,
            type: 'dashed',
            width: 1.5,
          },
        },
      },
      dataZoom: [
        {
          type: 'slider',
          xAxisIndex: 0,
          start: logs.length > 50 ? Math.max(0, ((logs.length - 50) / logs.length) * 100) : 0,
          end: 100,
          bottom: '5%',
          height: 30,
        },
        {
          type: 'inside',
          xAxisIndex: 0,
          start: logs.length > 50 ? Math.max(0, ((logs.length - 50) / logs.length) * 100) : 0,
          end: 100,
        },
      ],
      toolbox: {
        itemSize: 16,
        feature: {
          restore: {
            show: true,
            title: 'Restore',
          },
          saveAsImage: {
            show: true,
            title: 'Save as Image',
            name: 'chart',
            pixelRatio: 2,
          },
        },
        right: '1%',
        top: '12%',
      },
      graphic: [
        {
          type: 'text',
          left: '2%',
          bottom: '2%',
          style: {
            text: '💡 Dot size = Duration',
            fontSize: 13,
            fill: theme.colors.text.secondary,
            fontWeight: 500,
          },
        },
      ],
      series,
    };

    return chartOption as EChartsOption;
  }, [logs, theme, isMultiDayRange]);

  // Excel 다운로드 핸들러
  const handleExcelDownload = () => {
    if (excelExportRef.current) {
      excelExportRef.current();
    }
  };

  return (
    <TabLayout>
      {/* 날짜 범위 선택 */}
      <TabHeader dateRange={dateRange} onDateRangeChange={setDateRange} />

      {/* 에러 상태 */}
      {error && !loading && <ErrorMessage message={error} />}

      {loading ? (
        <LoadingSkeleton count={10} showChart={true} />
      ) : logs.length === 0 ? (
        <EmptyState message="No alarm logs in selected period" />
      ) : (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing[4],
            width: '100%',
          }}
        >
          <div
            style={{
              flex: 1,
              minHeight: 0,
              width: '100%',
              borderRadius: theme.borderRadius.md,
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing[5],
              }}
            >
              {/* 차트 */}
              <div
                style={{
                  width: '100%',
                  borderRadius: theme.borderRadius.md,
                  border: `1px solid ${theme.colors.border.container}`,
                  padding: theme.spacing[6],
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '400px',
                    position: 'relative',
                    maxWidth: '100%',
                    overflow: 'hidden',
                  }}
                >
                  <ReactECharts
                    ref={chartRef}
                    option={eventTimelineChartOption}
                    style={{ height: '100%', width: '100%', maxWidth: '100%' }}
                    opts={{ renderer: 'canvas', width: 'auto', height: 'auto' }}
                    notMerge={true}
                    lazyUpdate={true}
                  />
                </div>
              </div>

              {/* 테이블 */}
              <div
                style={{
                  width: '100%',
                  borderRadius: theme.borderRadius.md,
                  flexShrink: 0,
                }}
              >
                <DataTable
                  data={flattenedLogs}
                  columns={columns}
                  enablePagination={true}
                  pageSize={20}
                  pageSizeOptions={[10, 20, 50, 100]}
                  enableSorting={true}
                  emptyMessage="No event log data"
                  enableExcelExport={false}
                  excelFileName={`alarm_log_${device.serial_number}_${dateRange.from}_${dateRange.to}`}
                  onExcelExportReady={fn => {
                    excelExportRef.current = fn;
                  }}
                />
              </div>
            </div>
          </div>
          <DownloadButtons
            onDownloadExcel={handleExcelDownload}
            onDownloadPdf={onDownloadPdf}
            disabled={loading || logs.length === 0}
          />
        </div>
      )}
    </TabLayout>
  );
};
